import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { brand } from '../mock';

const navLinks = [
  { label: 'Platform', to: '/#platform' },
  { label: 'Who it\u2019s for', to: '/#audiences' },
  { label: 'Pricing', to: '/#pricing' },
  { label: 'Resources', to: '/#resources' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const handleAnchor = (to) => (e) => {
    if (to.startsWith('/#')) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className={`sticky top-0 z-50 w-full transition-colors duration-300 ${scrolled ? 'bg-white/90 backdrop-blur border-b border-stone-200' : 'bg-transparent'}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-9 w-9 rounded-xl bg-black overflow-hidden grid place-items-center shadow-sm group-hover:shadow-md transition-shadow">
              <img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/>
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-stone-900 text-[15px]">{brand.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500">{brand.tagline}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              l.to.startsWith('/#') ? (
                <a key={l.label} href={l.to} onClick={handleAnchor(l.to)} className="px-3 py-2 text-sm text-stone-700 hover:text-emerald-800 rounded-md hover:bg-stone-50 transition-colors">
                  {l.label}
                </a>
              ) : (
                <Link key={l.label} to={l.to} className="px-3 py-2 text-sm text-stone-700 hover:text-emerald-800 rounded-md hover:bg-stone-50 transition-colors">
                  {l.label}
                </Link>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="text-sky-700 hover:text-sky-800 hover:bg-sky-50">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white shadow-sm">Start Free Trial</Button>
            </Link>
          </div>

          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 rounded-md hover:bg-stone-100" aria-label="Toggle menu">
            {open ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((l) => (
              l.to.startsWith('/#') ? (
                <a key={l.label} href={l.to} onClick={handleAnchor(l.to)} className="block px-3 py-2 rounded-md text-stone-700 hover:bg-stone-50">{l.label}</a>
              ) : (
                <Link key={l.label} to={l.to} className="block px-3 py-2 rounded-md text-stone-700 hover:bg-stone-50">{l.label}</Link>
              )
            ))}
            <div className="pt-2 flex gap-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">Sign in</Button></Link>
              <Link to="/signup" className="flex-1"><Button className="w-full bg-emerald-800 hover:bg-emerald-900 text-white">Free Trial</Button></Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
